/**
 * Admin dashboard API client. Mirrors `backend/app/admin/` (+ `GET /me`).
 *
 * Snake_case at the wire boundary → camelCase in TS, same convention as
 * the rest of the app. The REAL authorization is server-side: every
 * `/admin/*` call 403s for non-admins regardless of the client `isAdmin`
 * flag — these wrappers just surface that.
 */

import { backendUrl } from "@/shared/config";
import { loadTokens } from "@/features/auth/lib/tokenStorage";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Me {
  userId: string;
  email: string | null;
  isAdmin: boolean;
}

export interface AdminOverview {
  users: {
    total: number;
    new7d: number;
    new30d: number;
    byStatus: Record<string, number>;
    byPlan: Record<string, number>;
  };
  revenue: { estimatedMrr: number; activeSubscriptions: number };
  engagement: {
    coachResponsesToday: number;
    coachResponsesMonth: number;
    coachResponsesTotal: number;
    activeUsers7d: number;
    conversationsTotal: number;
    conversationsNew7d: number;
    skillSignalsTotal: number;
    dailyCompletionsToday: number;
  };
  content: {
    librarySpots: number;
    underReviewSpots: number;
    feedbackUnread: number;
    feedbackTotal: number;
  };
  generatedAt: string;
}

export interface FunnelStage {
  key: string;
  label: string;
  count: number;
}

export interface FunnelStats {
  windowDays: number;
  since: string;
  cohortSize: number;
  stages: FunnelStage[];
  generatedAt: string;
}

export interface ReportedSpot {
  id: string;
  spot: Record<string, unknown>;
  contentHash: string | null;
  drillTarget: string | null;
  drillDifficulty: string | null;
  mode: string | null;
  reportedByUserId: string | null;
  notes: string | null;
  reportedAt: string | null;
  reportCount: number;
  restorable: boolean;
}

export interface FeedbackItem {
  id: string;
  text: string;
  pageUrl: string | null;
  conversationId: string | null;
  userId: string | null;
  createdAt: string | null;
  readAt: string | null;
  screenshotUrl: string | null;
}

export interface ActionResult {
  ok: boolean;
  detail: string;
}

export interface PageMeta {
  page: number;
  limit: number;
  total: number;
  hasNext: boolean;
}

export interface LibrarySpotItem {
  id: string;
  spot: Record<string, unknown>;
  contentHash: string | null;
  drillTarget: string | null;
  drillDifficulty: string | null;
  mode: string | null;
  sourceUserId: string | null;
  sourceConversationId: string | null;
  createdAt: string | null;
  facts: Record<string, unknown> | null;
  seedSeat: string | null;
  emittedPosition: string | null;
  seatMismatch: boolean;
}

export interface LibrarySpotsPage {
  items: LibrarySpotItem[];
  pageMeta: PageMeta;
}

/** The KIND of usage limit a user is under (mirrors backend `LimitState`). */
export type LimitState = "pre_report" | "walled" | "metered" | "uncapped";

export interface UserTelemetryItem {
  userId: string;
  email: string | null;
  createdAt: string | null;
  subscriptionStatus: string | null;
  planLevel: string | null;
  signalCount: number;
  coachResponsesTotal: number;
  coachResponses7d: number;
  conversationsTotal: number;
  lastActiveAt: string | null;
  dailyStreak: number;
  limitState: LimitState;
  monthlyCap: number | null;
  responsesThisMonth: number;
  capResetsAt: string | null;
  currentPeriodEnd: string | null;
}

export interface UserTelemetryPage {
  items: UserTelemetryItem[];
  pageMeta: PageMeta;
}

export interface UserCostBreakdown {
  inputTokens: number;
  outputTokens: number;
  cacheWriteTokens: number;
  cacheReadTokens: number;
  inputCost: number;
  outputCost: number;
  cacheWriteCost: number;
  cacheReadCost: number;
  totalCost: number;
}

export interface UserDetail {
  userId: string;
  email: string | null;
  createdAt: string | null;
  lastLoginAt: string | null;
  subscriptionStatus: string | null;
  planLevel: string | null;
  plan: string | null;
  isFounder: boolean;
  stripeCustomerId: string | null;
  currentPeriodEnd: string | null;
  limitState: LimitState;
  monthlyCap: number | null;
  responsesThisMonth: number;
  capResetsAt: string | null;
  signalCount: number;
  totalXp: number;
  dailyStreak: number;
  lastActiveAt: string | null;
  conversationsTotal: number;
  llmCallsTotal: number;
  meteredResponsesTotal: number;
  libraryServedTotal: number;
  costAllTime: UserCostBreakdown;
  costThisMonth: number;
  costToday: number;
  avgCostPerLlmCall: number;
}

export interface AdminAnnouncement {
  id: string;
  title: string;
  body: string;
  ctaLabel: string | null;
  ctaUrl: string | null;
  audienceKind: string; // "all" | "users"
  audienceEmails: string[];
  active: boolean;
  createdAt: string | null;
  unresolvedEmails: string[];
}

// ---------------------------------------------------------------------------
// Raw wire shapes + mappers (exported for tests)
// ---------------------------------------------------------------------------

interface RawMe {
  user_id: string;
  email: string | null;
  is_admin: boolean;
}
export function mapMe(raw: RawMe): Me {
  return { userId: raw.user_id, email: raw.email, isAdmin: raw.is_admin };
}

interface RawOverview {
  users: {
    total: number;
    new_7d: number;
    new_30d: number;
    by_status: Record<string, number>;
    by_plan: Record<string, number>;
  };
  revenue: { estimated_mrr: number; active_subscriptions: number };
  engagement: {
    coach_responses_today: number;
    coach_responses_month: number;
    coach_responses_total: number;
    active_users_7d: number;
    conversations_total: number;
    conversations_new_7d: number;
    skill_signals_total: number;
    daily_completions_today: number;
  };
  content: {
    library_spots: number;
    under_review_spots: number;
    feedback_unread: number;
    feedback_total: number;
  };
  generated_at: string;
}
export function mapOverview(raw: RawOverview): AdminOverview {
  return {
    users: {
      total: raw.users.total,
      new7d: raw.users.new_7d,
      new30d: raw.users.new_30d,
      byStatus: raw.users.by_status,
      byPlan: raw.users.by_plan,
    },
    revenue: {
      estimatedMrr: raw.revenue.estimated_mrr,
      activeSubscriptions: raw.revenue.active_subscriptions,
    },
    engagement: {
      coachResponsesToday: raw.engagement.coach_responses_today,
      coachResponsesMonth: raw.engagement.coach_responses_month,
      coachResponsesTotal: raw.engagement.coach_responses_total,
      activeUsers7d: raw.engagement.active_users_7d,
      conversationsTotal: raw.engagement.conversations_total,
      conversationsNew7d: raw.engagement.conversations_new_7d,
      skillSignalsTotal: raw.engagement.skill_signals_total,
      dailyCompletionsToday: raw.engagement.daily_completions_today,
    },
    content: {
      librarySpots: raw.content.library_spots,
      underReviewSpots: raw.content.under_review_spots,
      feedbackUnread: raw.content.feedback_unread,
      feedbackTotal: raw.content.feedback_total,
    },
    generatedAt: raw.generated_at,
  };
}

interface RawFunnelStats {
  window_days: number;
  since: string;
  cohort_size: number;
  stages: { key: string; label: string; count: number }[];
  generated_at: string;
}

export function mapFunnelStats(raw: RawFunnelStats): FunnelStats {
  return {
    windowDays: raw.window_days,
    since: raw.since,
    cohortSize: raw.cohort_size,
    stages: raw.stages.map((s) => ({ key: s.key, label: s.label, count: s.count })),
    generatedAt: raw.generated_at,
  };
}

interface RawReportedSpot {
  id: string;
  spot: Record<string, unknown>;
  content_hash: string | null;
  drill_target: string | null;
  drill_difficulty: string | null;
  mode: string | null;
  reported_by_user_id: string | null;
  notes: string | null;
  reported_at: string | null;
  report_count: number;
  restorable: boolean;
}
export function mapReportedSpot(raw: RawReportedSpot): ReportedSpot {
  return {
    id: raw.id,
    spot: raw.spot,
    contentHash: raw.content_hash,
    drillTarget: raw.drill_target,
    drillDifficulty: raw.drill_difficulty,
    mode: raw.mode,
    reportedByUserId: raw.reported_by_user_id,
    notes: raw.notes,
    reportedAt: raw.reported_at,
    reportCount: raw.report_count,
    restorable: raw.restorable,
  };
}

interface RawFeedback {
  id: string;
  text: string;
  page_url: string | null;
  conversation_id: string | null;
  user_id: string | null;
  created_at: string | null;
  read_at: string | null;
  screenshot_url: string | null;
}
export function mapFeedback(raw: RawFeedback): FeedbackItem {
  return {
    id: raw.id,
    text: raw.text,
    pageUrl: raw.page_url,
    conversationId: raw.conversation_id,
    userId: raw.user_id,
    createdAt: raw.created_at,
    readAt: raw.read_at,
    screenshotUrl: raw.screenshot_url,
  };
}

// ---------------------------------------------------------------------------
// HTTP (self-contained, mirrors features/billing/lib/billing.ts)
// ---------------------------------------------------------------------------

async function authedRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const tokens = loadTokens();
  if (!tokens) throw new Error("Not signed in");
  const res = await fetch(backendUrl(path), {
    ...init,
    headers: {
      ...(init.headers as Record<string, string> | undefined),
      Authorization: `Bearer ${tokens.accessToken}`,
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Admin request failed (${res.status}): ${text}`);
  }
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function getMe(): Promise<Me> {
  return mapMe(await authedRequest<RawMe>("/me"));
}

export async function getAdminOverview(): Promise<AdminOverview> {
  return mapOverview(await authedRequest<RawOverview>("/admin/overview"));
}

export async function getFunnel(days = 30): Promise<FunnelStats> {
  return mapFunnelStats(
    await authedRequest<RawFunnelStats>(`/admin/funnel?days=${days}`),
  );
}

export async function getReportedSpots(): Promise<ReportedSpot[]> {
  const raw = await authedRequest<RawReportedSpot[]>("/admin/reported-spots");
  return raw.map(mapReportedSpot);
}

export async function getAdminFeedback(unreadOnly = false): Promise<FeedbackItem[]> {
  const q = unreadOnly ? "?unread_only=true" : "";
  const raw = await authedRequest<RawFeedback[]>(`/admin/feedback${q}`);
  return raw.map(mapFeedback);
}

export async function dismissReportedSpot(id: string): Promise<ActionResult> {
  return authedRequest<ActionResult>(`/admin/reported-spots/${id}/dismiss`, {
    method: "POST",
  });
}

export async function restoreReportedSpot(id: string): Promise<ActionResult> {
  return authedRequest<ActionResult>(`/admin/reported-spots/${id}/restore`, {
    method: "POST",
  });
}

export async function markFeedbackRead(id: string): Promise<ActionResult> {
  return authedRequest<ActionResult>(`/admin/feedback/${id}/read`, {
    method: "POST",
  });
}

// ---------------------------------------------------------------------------
// Library spots browser + per-user telemetry (search/pagination views)
// ---------------------------------------------------------------------------

interface RawPageMeta {
  page: number;
  limit: number;
  total: number;
  has_next: boolean;
}
function mapPageMeta(raw: RawPageMeta): PageMeta {
  return {
    page: raw.page,
    limit: raw.limit,
    total: raw.total,
    hasNext: raw.has_next,
  };
}

interface RawLibrarySpot {
  id: string;
  spot: Record<string, unknown>;
  content_hash: string | null;
  drill_target: string | null;
  drill_difficulty: string | null;
  mode: string | null;
  source_user_id: string | null;
  source_conversation_id: string | null;
  created_at: string | null;
  facts: Record<string, unknown> | null;
  seed_seat: string | null;
  emitted_position: string | null;
  seat_mismatch: boolean;
}
export function mapLibrarySpot(raw: RawLibrarySpot): LibrarySpotItem {
  return {
    id: raw.id,
    spot: raw.spot,
    contentHash: raw.content_hash,
    drillTarget: raw.drill_target,
    drillDifficulty: raw.drill_difficulty,
    mode: raw.mode,
    sourceUserId: raw.source_user_id,
    sourceConversationId: raw.source_conversation_id,
    createdAt: raw.created_at,
    facts: raw.facts,
    seedSeat: raw.seed_seat,
    emittedPosition: raw.emitted_position,
    seatMismatch: raw.seat_mismatch,
  };
}

interface RawUserTelemetry {
  user_id: string;
  email: string | null;
  created_at: string | null;
  subscription_status: string | null;
  plan_level: string | null;
  signal_count: number;
  coach_responses_total: number;
  coach_responses_7d: number;
  conversations_total: number;
  last_active_at: string | null;
  daily_streak: number;
  limit_state: LimitState;
  monthly_cap: number | null;
  responses_this_month: number;
  cap_resets_at: string | null;
  current_period_end: string | null;
}
export function mapUserTelemetry(raw: RawUserTelemetry): UserTelemetryItem {
  return {
    userId: raw.user_id,
    email: raw.email,
    createdAt: raw.created_at,
    subscriptionStatus: raw.subscription_status,
    planLevel: raw.plan_level,
    signalCount: raw.signal_count,
    coachResponsesTotal: raw.coach_responses_total,
    coachResponses7d: raw.coach_responses_7d,
    conversationsTotal: raw.conversations_total,
    lastActiveAt: raw.last_active_at,
    dailyStreak: raw.daily_streak,
    limitState: raw.limit_state,
    monthlyCap: raw.monthly_cap,
    responsesThisMonth: raw.responses_this_month,
    capResetsAt: raw.cap_resets_at,
    currentPeriodEnd: raw.current_period_end,
  };
}

interface RawUserCostBreakdown {
  input_tokens: number;
  output_tokens: number;
  cache_write_tokens: number;
  cache_read_tokens: number;
  input_cost: number;
  output_cost: number;
  cache_write_cost: number;
  cache_read_cost: number;
  total_cost: number;
}
interface RawUserDetail {
  user_id: string;
  email: string | null;
  created_at: string | null;
  last_login_at: string | null;
  subscription_status: string | null;
  plan_level: string | null;
  plan: string | null;
  is_founder: boolean;
  stripe_customer_id: string | null;
  current_period_end: string | null;
  limit_state: LimitState;
  monthly_cap: number | null;
  responses_this_month: number;
  cap_resets_at: string | null;
  signal_count: number;
  total_xp: number;
  daily_streak: number;
  last_active_at: string | null;
  conversations_total: number;
  llm_calls_total: number;
  metered_responses_total: number;
  library_served_total: number;
  cost_all_time: RawUserCostBreakdown;
  cost_this_month: number;
  cost_today: number;
  avg_cost_per_llm_call: number;
}
export function mapUserDetail(raw: RawUserDetail): UserDetail {
  const c = raw.cost_all_time;
  return {
    userId: raw.user_id,
    email: raw.email,
    createdAt: raw.created_at,
    lastLoginAt: raw.last_login_at,
    subscriptionStatus: raw.subscription_status,
    planLevel: raw.plan_level,
    plan: raw.plan,
    isFounder: raw.is_founder,
    stripeCustomerId: raw.stripe_customer_id,
    currentPeriodEnd: raw.current_period_end,
    limitState: raw.limit_state,
    monthlyCap: raw.monthly_cap,
    responsesThisMonth: raw.responses_this_month,
    capResetsAt: raw.cap_resets_at,
    signalCount: raw.signal_count,
    totalXp: raw.total_xp,
    dailyStreak: raw.daily_streak,
    lastActiveAt: raw.last_active_at,
    conversationsTotal: raw.conversations_total,
    llmCallsTotal: raw.llm_calls_total,
    meteredResponsesTotal: raw.metered_responses_total,
    libraryServedTotal: raw.library_served_total,
    costAllTime: {
      inputTokens: c.input_tokens,
      outputTokens: c.output_tokens,
      cacheWriteTokens: c.cache_write_tokens,
      cacheReadTokens: c.cache_read_tokens,
      inputCost: c.input_cost,
      outputCost: c.output_cost,
      cacheWriteCost: c.cache_write_cost,
      cacheReadCost: c.cache_read_cost,
      totalCost: c.total_cost,
    },
    costThisMonth: raw.cost_this_month,
    costToday: raw.cost_today,
    avgCostPerLlmCall: raw.avg_cost_per_llm_call,
  };
}

export async function getLibrarySpots(args: {
  page?: number;
  limit?: number;
  spotId?: string;
}): Promise<LibrarySpotsPage> {
  const params = new URLSearchParams({
    page: String(args.page ?? 0),
    limit: String(args.limit ?? 25),
  });
  if (args.spotId) params.set("spot_id", args.spotId);
  const raw = await authedRequest<{ items: RawLibrarySpot[]; page_meta: RawPageMeta }>(
    `/admin/library-spots?${params.toString()}`,
  );
  return { items: raw.items.map(mapLibrarySpot), pageMeta: mapPageMeta(raw.page_meta) };
}

export async function getUsersTelemetry(args: {
  page?: number;
  limit?: number;
}): Promise<UserTelemetryPage> {
  const params = new URLSearchParams({
    page: String(args.page ?? 0),
    limit: String(args.limit ?? 25),
  });
  const raw = await authedRequest<{
    items: RawUserTelemetry[];
    page_meta: RawPageMeta;
  }>(`/admin/users?${params.toString()}`);
  return {
    items: raw.items.map(mapUserTelemetry),
    pageMeta: mapPageMeta(raw.page_meta),
  };
}

export async function getUserDetail(userId: string): Promise<UserDetail> {
  return mapUserDetail(
    await authedRequest<RawUserDetail>(`/admin/users/${encodeURIComponent(userId)}`),
  );
}

interface RawAdminAnnouncement {
  id: string;
  title: string;
  body: string;
  cta_label: string | null;
  cta_url: string | null;
  audience_kind: string;
  audience_emails: string[];
  active: boolean;
  created_at: string | null;
  unresolved_emails: string[];
}
export function mapAdminAnnouncement(raw: RawAdminAnnouncement): AdminAnnouncement {
  return {
    id: raw.id,
    title: raw.title,
    body: raw.body,
    ctaLabel: raw.cta_label,
    ctaUrl: raw.cta_url,
    audienceKind: raw.audience_kind,
    audienceEmails: raw.audience_emails,
    active: raw.active,
    createdAt: raw.created_at,
    unresolvedEmails: raw.unresolved_emails,
  };
}

export async function getAdminAnnouncements(): Promise<AdminAnnouncement[]> {
  const raw = await authedRequest<RawAdminAnnouncement[]>("/admin/announcements");
  return raw.map(mapAdminAnnouncement);
}

export async function createAnnouncement(args: {
  title: string;
  body: string;
  ctaLabel?: string;
  ctaUrl?: string;
  audienceEmails: string[];
}): Promise<AdminAnnouncement> {
  const raw = await authedRequest<RawAdminAnnouncement>("/admin/announcements", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      title: args.title,
      body: args.body,
      cta_label: args.ctaLabel || null,
      cta_url: args.ctaUrl || null,
      audience_emails: args.audienceEmails,
    }),
  });
  return mapAdminAnnouncement(raw);
}

export async function setAnnouncementActive(
  id: string,
  active: boolean,
): Promise<ActionResult> {
  return authedRequest<ActionResult>(
    `/admin/announcements/${id}/set-active?active=${active}`,
    { method: "POST" },
  );
}
